import { Set } from 'immutable';
import { createSelector } from 'reselect';
import { getProduct, getName, getFinish, getCustom, getCustomValue, getOrderedFields, getOrderedFieldsArray, getFinishOptions, getCustomOptions, selectedFieldOption, getWoodThumbnail, getDistressThumbnail, getDefaultProps, getDefaultPropsByFieldName, addSuffixForValidFieldValue } from './index';
import * as fieldName from '../constants/Fields';
import * as mouldingFieldName from '../constants/MouldingFields';

const getHeadingA = (state) => state.getIn(['fields', mouldingFieldName.HEADING_A]);
const getThicknessA = (state) => state.getIn(['fields', mouldingFieldName.THICKNESS_A]);
const getWidthA = (state) => state.getIn(['fields', mouldingFieldName.WIDTH_A]);
const getLength = (state) => state.getIn(['fields', mouldingFieldName.LENGTH]);
const getQuantityA = (state) => state.getIn(['fields', mouldingFieldName.QUANTITY_A]);
const getHeadingB = (state) => state.getIn(['fields', mouldingFieldName.HEADING_B]);
const getThicknessB = (state) => state.getIn(['fields', mouldingFieldName.THICKNESS_B]);
const getWidthB = (state) => state.getIn(['fields', mouldingFieldName.WIDTH_B]);
const getQuantityB = (state) => state.getIn(['fields', mouldingFieldName.QUANTITY_B]);
const getThicknessACaption = (state) => state.getIn(['product', mouldingFieldName.THICKNESS_A_CAPTION]);
const getThicknessBCaption = (state) => state.getIn(['product', mouldingFieldName.THICKNESS_B_CAPTION]);
const getWidthACaption = (state) => state.getIn(['product', mouldingFieldName.WIDTH_A_CAPTION]);
const getWidthBCaption = (state) => state.getIn(['product', mouldingFieldName.WIDTH_B_CAPTION]);
const getLengthCaption = (state) => state.getIn(['product', mouldingFieldName.LENGTH_CAPTION]);
const getQuantityUnits = (state) => state.getIn(['product', mouldingFieldName.QUANTITY_UNITS]);
const getQuantityCaption = (state) => state.getIn(['product', mouldingFieldName.UNITS_CAPTION]);
const getThicknessADefault = (state) => state.getIn(['product', mouldingFieldName.THICKNESS_A_DEFAULT]);
const getWidthADefault = (state) => state.getIn(['product', mouldingFieldName.WIDTH_A_DEFAULT]);
const getThicknessBDefault = (state) => state.getIn(['product', mouldingFieldName.THICKNESS_B_DEFAULT]);
const getWidthBDefault = (state) => state.getIn(['product', mouldingFieldName.WIDTH_B_DEFAULT]);
const getLengthDefault = (state) => state.getIn(['product', mouldingFieldName.LENGTH_DEFAULT]);

const getHasBase = createSelector(
  [getThicknessADefault, getWidthADefault, getLengthDefault],
  (thickness, width, length) => {
    return (thickness || width || length) ? true : false;
  }
);

const getHasCase = createSelector(
  [getThicknessBDefault, getWidthBDefault],
  (thickness, width) => {
    return (thickness || width) ? true : false;
  }
);

const getHasBaseAndCase = createSelector(
  [getHasBase, getHasCase],
  (hasBase, hasCase) => {
    return hasBase && hasCase;
  }
);

const getHeadingAOptions = createSelector(
  [getHeadingA, getHasBaseAndCase],
  (heading, hasBaseAndCase) => {
    let calculatedProps = {};

    if (!hasBaseAndCase) {
      calculatedProps = {
        hide: true,
        summaryText: null
      }
    }

    return Object.assign({}, getDefaultProps(heading), calculatedProps);
  }
);

const getThicknessAOptions = createSelector(
  [getThicknessA, getThicknessADefault, getThicknessACaption, getHasBaseAndCase],
  (thickness, defaultThickness, thicknessCaption, hasBaseAndCase) => {
    let calculatedProps = { note: thicknessCaption };

    if (!defaultThickness) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Base: ' : '';
      const summary = addSuffixForValidFieldValue(thickness, ' Thick (A)');
      calculatedProps.summaryText = summary ? prefix + summary : null;
    }

    return Object.assign({}, getDefaultProps(thickness), calculatedProps);
  }
);

const getWidthAOptions = createSelector(
  [getProduct, getWidthA, getWidthADefault, getWidthACaption, getHasBaseAndCase],
  (moulding, width, defaultWidth, widthCaption, hasBaseAndCase) => {
    let calculatedProps = { note: widthCaption };

    if (!defaultWidth) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Base: ' : '';
      const summary = addSuffixForValidFieldValue(width, ' Wide (B)');
      calculatedProps.summaryText = summary ? prefix + summary : null;
    }

    return Object.assign({}, getDefaultProps(width), calculatedProps);
  }
);

const getLengthOptions = createSelector(
  [getProduct, getLength, getLengthDefault, getLengthCaption, getHasBaseAndCase],
  (moulding, length, defaultLength, lengthCaption, hasBaseAndCase) => {
    let calculatedProps = { note: lengthCaption };

    if (!defaultLength) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Base: ' : '';
      const summary = addSuffixForValidFieldValue(length, ' Long (C)');
      calculatedProps.summaryText = summary ? prefix + summary : null;
    }

    return Object.assign({}, getDefaultProps(length), calculatedProps);
  }
);

const getQuantityAOptions = createSelector(
  [getQuantityA, getQuantityUnits, getQuantityCaption, getHasBaseAndCase],
  (quantity, units, quantityCaption, hasBaseAndCase) => {
    const quantityValue = quantity.get('value');
    let calculatedProps = { note: quantityCaption };

    if (!units) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Base: ' : '';
      const isLengthQuantity = units.toLowerCase() === 'linear feet';
      const unitsLabel = isLengthQuantity ? 'in Linear Feet' : units.toLowerCase();
      
      if (!isLengthQuantity) {
        calculatedProps.tooltip = quantity.get('tooltip_each');
      }
      
      calculatedProps.label = `Quantity (${unitsLabel})?`;
      calculatedProps.summaryText = quantityValue ? prefix + addSuffixForValidFieldValue(quantity, ` ${units}`) : null;
    }

    return Object.assign({}, getDefaultProps(quantity), calculatedProps);
  }
);

const getHeadingBOptions = createSelector(
  [getHeadingB, getHasBaseAndCase],
  (heading, hasBaseAndCase) => {
    let calculatedProps = {};

    if (!hasBaseAndCase) {
      calculatedProps = {
        hide: true,
        summaryText: null
      }
    }

    return Object.assign({}, getDefaultProps(heading), calculatedProps);
  }
);

const getThicknessBOptions = createSelector(
  [getThicknessB, getThicknessBDefault, getThicknessBCaption, getHasBaseAndCase],
  (thickness, defaultThickness, thicknessCaption, hasBaseAndCase) => {
    let calculatedProps = { note: thicknessCaption };

    if (!defaultThickness) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Case: ' : '';
      const summary = addSuffixForValidFieldValue(thickness, ' Thick (A)');
      calculatedProps.summaryText = summary ? prefix + summary : null;
    }

    return Object.assign({}, getDefaultProps(thickness), calculatedProps);
  }
);

const getWidthBOptions = createSelector(
  [getProduct, getWidthB, getWidthBDefault, getWidthBCaption, getHasBaseAndCase],
  (moulding, width, defaultWidth, widthCaption, hasBaseAndCase) => {
    let calculatedProps = { note: widthCaption };

    if (!defaultWidth || !hasBaseAndCase) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Case: ' : '';
      const summary = addSuffixForValidFieldValue(width, ' Wide (B)');
      calculatedProps.summaryText = summary ? prefix + summary : null;
    }

    return Object.assign({}, getDefaultProps(width), calculatedProps);
  }
);

const getQuantityBOptions = createSelector(
  [getProduct, getQuantityB, getQuantityUnits, getQuantityCaption, getHasBaseAndCase],
  (moulding, quantity, units, quantityCaption, hasBaseAndCase) => {
    const quantityValue = quantity.get('value');
    let calculatedProps = { note: quantityCaption };

    if (!units || !hasBaseAndCase) {
      calculatedProps.hide = true;
      calculatedProps.summaryText = null;
    } else {
      const prefix = hasBaseAndCase ? 'Case: ' : '';
      const isLengthQuantity = units.toLowerCase() === 'linear feet';
      const unitsLabel = isLengthQuantity ? 'in Linear Feet' : units.toLowerCase();
      
      if (!isLengthQuantity) {
        calculatedProps.tooltip = quantity.get('tooltip_each');
      }
      
      calculatedProps.label = `Quantity (${unitsLabel})?`;
      calculatedProps.summaryText = quantityValue ? prefix + addSuffixForValidFieldValue(quantity, ` ${units}`) : null;
    }

    return Object.assign({}, getDefaultProps(quantity), calculatedProps);
  }
);

export const getMouldingFieldProps = (state, name) => {
  switch (name) {
    case mouldingFieldName.HEADING_A:
      return getHeadingAOptions(state);
    case mouldingFieldName.THICKNESS_A:
      return getThicknessAOptions(state);
    case mouldingFieldName.WIDTH_A:
      return getWidthAOptions(state);
    case mouldingFieldName.LENGTH:
      return getLengthOptions(state);
    case mouldingFieldName.QUANTITY_A:
      return getQuantityAOptions(state);
    case mouldingFieldName.HEADING_B:
      return getHeadingBOptions(state);
    case mouldingFieldName.THICKNESS_B:
      return getThicknessBOptions(state);
    case mouldingFieldName.WIDTH_B:
      return getWidthBOptions(state);
    case mouldingFieldName.QUANTITY_B:
      return getQuantityBOptions(state);
    case fieldName.FINISH:
      return getFinishOptions(state);
    case fieldName.CUSTOM:
      return getCustomOptions(state);
  }

  return getDefaultPropsByFieldName(state, name);
};

export const getMouldingSelectedOption = (state, name) => {
  switch(name) {
    case fieldName.WOOD:
      return getWoodThumbnail(state);
    case fieldName.DISTRESS:
      return getDistressThumbnail(state);
  }
};

export const getIllustration = createSelector(
  [getProduct],
  (product) => product.get('image')
);

export const getAltText = createSelector(
  [getProduct],
  (product) => product.get('imageAlt')
);

export const getMouldingSummaryFieldValues = (state) => {
  return getOrderedFieldsArray(state).map((name) => {
    const props = getMouldingFieldProps(state, name);
    return (props.hide || props.disabled.indexOf(props.selected) >= 0) ? false : props.summaryText;
  }).filter((displayValue) => displayValue ? true : false);
};

export const getMouldingOptionThumbnails = (state) => {
  const thumbnailFields = [fieldName.WOOD, fieldName.DISTRESS];
  return thumbnailFields.map((name) => {
    const fieldProps = getMouldingFieldProps(state, name),
          disabledOptions = fieldProps.disabled,
          isDisabled = disabledOptions && disabledOptions.indexOf(fieldProps.selected) > -1,
          isHidden = fieldProps.hide === true;
    
    return (isDisabled || isHidden) ? {} : getMouldingSelectedOption(state, name);
  }).filter(thumbnail => thumbnail.image ? true : false);
};
